import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import Instance from 'providers/instance';
import remoteIpInstance from 'providers/remote-ip';
import { userAgent } from 'providers/fingerprint';

import { putErrorResponse } from 'middleware/actions/error';
import { paymentEndpoint as endpoint } from 'providers/endpoints/payment';
import { formatTransferConfirmation as formatTransactionResponse } from 'utils/transfer/formatter';
import {
  actions,
  putUtilityFavouriteDetail,
  putUtilityFavouriteConfirm,
  putUtilityFavouriteResult,
  putUtilityFavouriteTac,
} from 'middleware/actions/favourite/payment';
import { putUtilityBillerDetail, putUtilityReceipt, putUtilityTac } from 'middleware/actions/payment';
import { formatKeys } from 'utils/formatter';
import { RECEIPT_KEYMAP } from 'settings/constants/keymap';

const getClientIP = () => remoteIpInstance.get();

const newFavouriteDetail = ({ body }) => Instance.post(endpoint.utility.favourite.add.getDetail, body);
const newFavouriteDetailFromPayment = ({ body }) => Instance.post(endpoint.utility.favourite.add.getDetailFromPayment, body);
const newFavouriteConfirmation = ({ body }) => Instance.post(endpoint.utility.favourite.add.getConfirmation, body);
const newFavouriteTac = ({ body }) => Instance.post(endpoint.utility.favourite.add.getTac, body);
const newFavouriteResult = ({ body }) => Instance.post(endpoint.utility.favourite.add.getResult, body);
const getEditDetail = ({ body }) => Instance.post(endpoint.utility.favourite.edit.getDetail, body);
const getEditConfirmation = ({ body }) => Instance.post(endpoint.utility.favourite.edit.getConfirmation, body);
const getEditTac = ({ body }) => Instance.post(endpoint.utility.favourite.edit.getTac, body);
const getEditResult = ({ body }) => Instance.post(endpoint.utility.favourite.edit.getResult, body);
const getDeleteConfirm = ({ body }) => Instance.post(endpoint.utility.favourite.delete.getConfirmation, body);
const getDeleteTac = ({ body }) => Instance.post(endpoint.utility.favourite.delete.getTac, body);
const getDeleteResult = ({ body }) => Instance.post(endpoint.utility.favourite.delete.getResult, body);
const getPaymentDetail = ({ body }) => Instance.post(endpoint.utility.favourite.payment.getDetail, body);
const getPaymentTac = ({ body }) => Instance.post(endpoint.otherBill.favourite.payment.getTac, body);
const getPaymentReceipt = ({ body }) => Instance.post(endpoint.otherBill.favourite.payment.getReceipt, body);

const getLocale = state => state.LanguageReducer.locale;

const { CONFIRM: DELETE_CONFIRM, TAC: DELETE_TAC, RESULT: DELETE_RESULT } = actions.GET.UTILITY.DELETE_FAVOURITE;
const { DETAIL: EDIT_DETAIL, CONFIRM: EDIT_CONFIRM, TAC: EDIT_TAC, RESULT: EDIT_RESULT } = actions.GET.UTILITY.EDIT_FAVOURITE;
const {
  DETAIL: ADD_DETAIL,
  DETAIL_FROM_PAYMENT,
  CONFIRM: ADD_CONFIRM,
  TAC: ADD_TAC,
  RESULT: ADD_RESULT,
} = actions.GET.UTILITY.ADD_FAVOURITE;
const { DETAIL: PAYMENT_DETAIL, TAC: PAYMENT_TAC, RECEIPT: PAYMENT_RECEIPT } = actions.GET.UTILITY.PAYMENT_FROM_FAVOURITE;

function* getNewFavouriteUtilityDetailSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(newFavouriteDetail, { body });
    yield put(putUtilityFavouriteDetail(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getNewFavouriteUtilityDetailFromPaymentSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(newFavouriteDetailFromPayment, { body });
    yield put(putUtilityFavouriteDetail(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* utilityConfirmationFavouriteSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(newFavouriteConfirmation, { body });
    yield put(putUtilityFavouriteConfirm(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getEditDetailSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(getEditDetail, { body });
    yield put(putUtilityFavouriteDetail(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getEditConfirmSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(getEditConfirmation, { body });
    yield put(putUtilityFavouriteConfirm(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getDeleteConfirmSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(getDeleteConfirm, { body });
    yield put(putUtilityFavouriteConfirm(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getCommonTacSaga({ payload, type }) {
  try {
    const locale = yield select(getLocale);
    const { ip: remoteIPAddress } = yield call(getClientIP);

    const body = { locale, remoteIPAddress, userAgent, ...payload };

    const handler = {
      [ADD_TAC]: () => newFavouriteTac,
      [DELETE_TAC]: () => getDeleteTac,
      [EDIT_TAC]: () => getEditTac,
      [PAYMENT_TAC]: () => getPaymentTac,
      default: () => null,
    };

    const tacEndpoint = (handler[type] || handler.default)();

    const { data } = yield call(tacEndpoint, { body });
    yield put(putUtilityFavouriteTac(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getCommonResultSaga({ payload, type }) {
  try {
    const locale = yield select(getLocale);
    const { ip: remoteIPAddress } = yield call(getClientIP);
    const body = { locale, remoteIPAddress, userAgent, ...payload };

    const handler = {
      [ADD_RESULT]: () => newFavouriteResult,
      [DELETE_RESULT]: () => getDeleteResult,
      [EDIT_RESULT]: () => getEditResult,
      default: () => null,
    };

    const resultEndpoint = (handler[type] || handler.default)();

    const { data } = yield call(resultEndpoint, { body });
    yield put(putUtilityFavouriteResult(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

// Payment flow from favourite list
function* getPaymentDetailSaga({ payload }) {
  try {
    const locale = yield select(getLocale);
    const body = { ...payload, locale };
    const { data } = yield call(getPaymentDetail, { body });
    yield put(putUtilityBillerDetail(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getPaymentTacSaga({ payload = {} }) {
  try {
    const locale = yield select(getLocale);
    const { ip: remoteIPAddress } = yield call(getClientIP);
    const body = { locale, remoteIPAddress, userAgent, ...payload };

    const { data } = yield call(getPaymentTac, { body });
    yield put(putUtilityTac(formatTransactionResponse(data)));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

function* getPaymentReceiptSaga({ payload = {} }) {
  try {
    const locale = yield select(getLocale);
    const initialBody = { locale, ...payload };
    const body = formatKeys(initialBody, RECEIPT_KEYMAP);
    const { data } = yield call(getPaymentReceipt, { body });

    yield put(putUtilityReceipt(data));
  } catch (error) {
    yield put(putErrorResponse(error));
  }
}

export default function* CreditCardFavourite() {
  yield all([
    takeLatest(ADD_DETAIL, getNewFavouriteUtilityDetailSaga),
    takeLatest(DETAIL_FROM_PAYMENT, getNewFavouriteUtilityDetailFromPaymentSaga),
    takeLatest(ADD_CONFIRM, utilityConfirmationFavouriteSaga),
    takeLatest(ADD_TAC, getCommonTacSaga),
    takeLatest(ADD_RESULT, getCommonResultSaga),
    takeLatest(EDIT_DETAIL, getEditDetailSaga),
    takeLatest(EDIT_CONFIRM, getEditConfirmSaga),
    takeLatest(EDIT_TAC, getCommonTacSaga),
    takeLatest(EDIT_RESULT, getCommonResultSaga),
    takeLatest(DELETE_CONFIRM, getDeleteConfirmSaga),
    takeLatest(DELETE_TAC, getCommonTacSaga),
    takeLatest(DELETE_RESULT, getCommonResultSaga),
    takeLatest(PAYMENT_DETAIL, getPaymentDetailSaga),
    takeLatest(PAYMENT_TAC, getPaymentTacSaga),
    takeLatest(PAYMENT_RECEIPT, getPaymentReceiptSaga),
  ]);
}
